/**
 * Diagram Type Selector Component
 * Visual selector for diagram types with enabled/disabled states
 */

import { memo } from 'react'

export interface DiagramTypeOption {
  type: string
  label: string
  icon: string
  description: string
  enabled: boolean
  comingSoon?: boolean
}

interface DiagramTypeSelectorProps {
  value: string
  onChange: (type: string) => void
  options: DiagramTypeOption[]
}

function DiagramTypeSelector({ value, onChange, options }: DiagramTypeSelectorProps) {
  const handleKeyDown = (e: React.KeyboardEvent, option: DiagramTypeOption) => {
    if (!option.enabled) return

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onChange(option.type)
    }
  }

  return (
    <div className="diagram-type-grid" role="radiogroup" aria-label="Diagram type">
      {options.map((option) => (
        <label
          key={option.type}
          className={`diagram-type-card ${value === option.type ? 'selected' : ''} ${
            !option.enabled ? 'disabled' : ''
          }`}
          tabIndex={option.enabled ? 0 : -1}
          onKeyDown={(e) => handleKeyDown(e, option)}
          aria-disabled={!option.enabled}
        >
          <input
            type="radio"
            name="diagram-type"
            value={option.type}
            checked={value === option.type}
            onChange={(e) => onChange(e.target.value)}
            disabled={!option.enabled}
            aria-label={`${option.label} - ${option.description}`}
          />

          <span className="diagram-type-icon">{option.icon}</span>
          <span className="diagram-type-label">{option.label}</span>
          <span className="diagram-type-description">{option.description}</span>

          {option.comingSoon && (
            <span className="coming-soon-badge">Coming Soon</span>
          )}
        </label>
      ))}
    </div>
  )
}

export default memo(DiagramTypeSelector)
